import { formatNumber } from '@pmai/contracts';
import type { ReactNode } from 'react';
import { Button } from './button';
import { cn } from './cn';
import { Spinner } from './feedback';
import { useUiStrings } from './strings';

export interface Column<T> {
  key: string;
  header: string;
  /** `index` — position among the displayed rows (row numbers, FR-DASH-008). */
  cell: (row: T, index: number) => ReactNode;
  /** Footer cell (totals, FR-TASK-010); a footer row is drawn when any column has one. */
  footer?: ReactNode;
  /** Server-side sort key; omit for unsortable columns. */
  sortKey?: string;
  align?: 'left' | 'right';
  className?: string;
}

export interface SortState {
  sort: string;
  order: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  label: string;
  columns: readonly Column<T>[];
  rows: readonly T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Shown when there are no rows (FR-CUST-004 style "nothing found"). */
  empty: ReactNode;
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  paging?: PagingState;
  /** Extra classes per row (done, overdue, archived …). */
  rowClassName?: (row: T) => string | undefined;
}

/**
 * Every list in the app (engineering.md §3): server-side sorting and paging driven by the shared
 * list contract (`listQuerySchema`). Pages configure columns; they never draw a <table>.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  loading,
  empty,
  sort,
  onSortChange,
  paging,
  rowClassName,
}: DataTableProps<T>) {
  const s = useUiStrings();
  const toggle = (key: string) =>
    onSortChange?.({ sort: key, order: sort?.sort === key && sort.order === 'asc' ? 'desc' : 'asc' });

  if (loading && !rows) return <Spinner label={s.loading} />;
  if (!rows || rows.length === 0) return <div className="py-2">{empty}</div>;

  return (
    <div className="overflow-hidden rounded-card border border-border bg-surface">
      <div className="overflow-x-auto">
        <table aria-label={label} aria-busy={loading || undefined} className="w-full text-left text-sm">
          <thead className="border-b border-border bg-canvas/60 text-xs uppercase tracking-wide text-ink-muted">
            <tr>
              {columns.map((c) => {
                const active = c.sortKey !== undefined && sort?.sort === c.sortKey;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={active ? (sort?.order === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      'px-4 py-2.5 font-medium',
                      c.align === 'right' && 'text-right',
                      c.className,
                    )}
                  >
                    {c.sortKey && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggle(c.sortKey!)}
                        className="inline-flex items-center gap-1 uppercase hover:text-ink"
                      >
                        {c.header}
                        <span aria-hidden className={cn(!active && 'opacity-0')}>
                          {sort?.order === 'desc' ? '▼' : '▲'}
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, index) => (
              <tr key={rowKey(row)} className={cn('hover:bg-canvas/50', rowClassName?.(row))}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-column={c.key}
                    className={cn('px-4 py-3 align-middle', c.align === 'right' && 'text-right', c.className)}
                  >
                    {c.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {columns.some((c) => c.footer !== undefined) && (
            <tfoot className="border-t border-border bg-canvas/60 font-medium">
              <tr>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-column={c.key}
                    className={cn('px-4 py-2.5', c.align === 'right' && 'text-right', c.className)}
                  >
                    {c.footer}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {paging && <Pager {...paging} className="border-t border-border" />}
    </div>
  );
}

export interface PagingState {
  offset: number;
  limit: number;
  total: number;
  onChange: (offset: number) => void;
}

/** "1–50 no 120" with previous/next — hidden when everything fits on one page. */
export function Pager({ offset, limit, total, onChange, className }: PagingState & { className?: string }) {
  const s = useUiStrings();
  if (total <= limit) return null;
  return (
    <div
      className={cn('flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-ink-muted', className)}
    >
      <span>
        {s.pageRange(
          formatNumber(offset + 1),
          formatNumber(Math.min(offset + limit, total)),
          formatNumber(total),
        )}
      </span>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          {s.previousPage}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={offset + limit >= total}
          onClick={() => onChange(offset + limit)}
        >
          {s.nextPage}
        </Button>
      </div>
    </div>
  );
}
